import moment from 'moment';
import { useSelector } from 'react-redux';

import { MilestoneItem, DayTileItem } from 'pages/Calendar/components';
import { Icons } from 'pages/Calendar/Icons';

import { formatDate } from 'helpers';
import { isEqual, isNumber } from 'lodash-es';
import {
  CalendarEventKey,
  CalendarEventLabel,
  defaultCalendarTypeOptions,
  userTimezoneMap,
} from 'pages/Calendar/constants';
import { getCalendarFiltersAppliedSelector } from 'store/selectors/calendar';
import { getUserTimeZone } from 'store/selectors/user';

import styles from './styles.module.scss';

const EmptyState = ({ isFiltersApplied = false }) => (
  <div className={styles.emptyStats}>
    <Icons variant={isFiltersApplied ? Icons.EMPTY_CALENDAR_2 : Icons.EMPTY_CALENDAR} />
    <p>
      {isFiltersApplied ? (
        <span>
          No calendar entries were found.
          <br /> Please try changing or removing your selected filters.
        </span>
      ) : (
        "Your calendar's open. Might be a good day to catch up (or get ahead)."
      )}
    </p>
  </div>
);

export const DayView = ({ date, calendarData, isOverdueMilestones }) => {
  const dateItem = calendarData.find((item) => item.date === date);
  const filters = useSelector(getCalendarFiltersAppliedSelector);
  const userTimezone = useSelector(getUserTimeZone);

  if (
    !isOverdueMilestones &&
    (!dateItem ||
      Object.entries(dateItem)
        .filter(([key]) => key !== 'date')
        .every(([, value]: any) => typeof value === 'object' && value?.count === 0))
  ) {
    const isFiltersApplied = !isEqual(filters, {
      type: defaultCalendarTypeOptions,
      agentsOrOwners: [],
      clients: [],
      transactionIds: [],
    });

    return <EmptyState isFiltersApplied={isFiltersApplied} />;
  }

  const rowTypes = [
    {
      key: CalendarEventKey.MILESTONES,
      className: styles.milestones,
    },
    { key: CalendarEventKey.TASKS, className: styles.tasks },
    {
      key: CalendarEventKey.SHOWINGS,
      className: styles.showings,
    },
    {
      key: CalendarEventKey.TOURS,
      className: styles.tours,
    },
    {
      key: CalendarEventKey.CLOSINGS,
      className: styles.closing,
    },
    {
      key: CalendarEventKey.PROJECT_KICKOFFS,
      className: styles.projects,
    },
    {
      key: CalendarEventKey.EXPIRING_OFFERS,
      className: styles.expiring,
    },
    {
      key: CalendarEventKey.EXPIRING_LISTINGS,
      className: styles.expiring,
    },
    {
      key: CalendarEventKey.EXPIRING_PROJECTS,
      className: styles.expiring,
    },
  ];

  const getTitle = (key, title) => {
    if (key === CalendarEventKey.EXPIRING_PROJECTS) return CalendarEventLabel.EXPIRING_PROJECTS;
    else if (key === CalendarEventKey.PROJECT_KICKOFFS) return CalendarEventLabel.PROJECT_KICKOFFS;
    else return title;
  };

  const getSubtitle = (key, item) => {
    const address = item?.address?.Address?.PlaceName?.replace('USA', item?.address?.Address?.Zip);

    if (key === CalendarEventKey.EXPIRING_PROJECTS || key === CalendarEventKey.PROJECT_KICKOFFS)
      return item?.title;
    else if (key === CalendarEventKey.TOURS)
      return `${item?.address?.Line1}, ${item?.address?.City}, ${item?.address?.State}, ${item?.address?.Zip}`;
    else return address || item?.subtitle;
  };

  const getTaskTime = (item) => {
    const { hours, minutes } = item || {};

    if (isNumber(hours) && isNumber(minutes)) {
      const time = moment({ hour: hours, minute: minutes }).toDate();
      return `${formatDate(time, 'hh:mm A')}`;
    }

    return null;
  };

  const getTourTime = (item) => `${formatDate(item?.dueDate, 'hh:mm A')}`;

  const getStartTime = (key, item) => {
    if (key === CalendarEventKey.TASKS) {
      return getTaskTime(item);
    } else if (key === CalendarEventKey.TOURS) {
      return getTourTime(item);
    } else {
      return item?.startTime ? formatDate(item.startTime, 'hh:mm A') : null;
    }
  };

  const getSortTime = (key, item) => {
    if (key === CalendarEventKey.TASKS) {
      const { hours, minutes } = item;
      const baseDate = moment(item?.dueDate);
      if (isNumber(hours) && isNumber(minutes)) {
        return baseDate.clone().hour(hours).minute(minutes).second(0).toDate();
      }
    } else if (key === CalendarEventKey.TOURS) {
      return item?.dueDate ? new Date(item.dueDate) : null;
    } else {
      return item?.startTime ? new Date(item.startTime) : null;
    }
    return null;
  };

  const allEvents = !isOverdueMilestones
    ? rowTypes.flatMap(({ key, className }) => {
        const items = dateItem[key]?.values || [];
        return items.map((item) => ({
          ...item,
          key,
          className,
        }));
      })
    : [];

  // Sort: timed items first in ascending time, then all-day items
  const sortedEvents = !isOverdueMilestones
    ? allEvents.sort((a, b) => {
        const aTime = getSortTime(a.key, a);
        const bTime = getSortTime(b.key, b);

        if (!aTime && !bTime) return 0;
        if (!aTime) return 1; // a is all-day, goes later
        if (!bTime) return -1; // b is all-day, goes later

        return aTime.getTime() - bTime.getTime();
      })
    : [];

  // Overdue Milestone View
  if (isOverdueMilestones) {
    if (calendarData?.length === 0) return <EmptyState />;

    return (
      <div className={styles.dayViewContainer}>
        {calendarData?.map((item, idx) => (
          <MilestoneItem
            key={idx}
            title={item?.title}
            subtitle={
              item?.address
                ? item?.address?.Address?.PlaceName?.replace('USA', item?.address?.Address?.Zip)
                : item?.subtitle
            }
            milestoneId={item?.Id}
            client={item?.clients}
            agent={item?.agents}
            isKeyDate={item?.IsKeyDate}
            isEditable={item?.IsEditable}
            isProject={item?.IsProject}
            isCreator={item?.IsTransactionCreator}
            status={item?.Status}
            transactionId={item?.TransactionId}
            participants={item?.Participants}
            dueDate={item?.dueDate}
          />
        ))}
      </div>
    );
  }

  return (
    <div className={styles.dayViewContainer}>
      {sortedEvents.map((item, index) =>
        item.key === CalendarEventKey.MILESTONES ? (
          <MilestoneItem
            key={`${item.key}-${index}`}
            title={item?.title}
            subtitle={
              item?.address
                ? item?.address?.Address?.PlaceName?.replace('USA', item?.address?.Address?.Zip)
                : item?.subtitle
            }
            milestoneId={item?.Id}
            client={item?.clients}
            agent={item?.agents}
            isKeyDate={item?.IsKeyDate}
            isEditable={item?.IsEditable}
            isProject={item?.IsProject}
            isCreator={item?.IsTransactionCreator}
            status={item?.Status}
            transactionId={item?.TransactionId}
            participants={item?.Participants}
          />
        ) : (
          <DayTileItem
            key={`${item.key}-${index}`}
            id={item?.id}
            transactionId={item?.transactionId}
            campaignId={item?.campaignId}
            uuid={item?.uuid}
            type={item?.key}
            title={getTitle(item.key, item?.title)}
            subtitle={getSubtitle(item.key, item)}
            startTime={getStartTime(item.key, item)}
            endTime={item.endTime ? formatDate(item.endTime, 'hh:mm A') : null}
            client={item?.clients}
            agentsOrAssignees={item.key !== CalendarEventKey.TASKS ? item?.agents : item?.assignees}
            className={item.className}
            isTasks={item.key === CalendarEventKey.TASKS}
            isProject={
              item.key === CalendarEventKey.EXPIRING_PROJECTS ||
              item.key === CalendarEventKey.PROJECT_KICKOFFS
            }
          />
        ),
      )}
      <div className={styles.timezoneLabel}>
        <p>Time Zone: {userTimezoneMap?.[userTimezone]}</p>
      </div>
    </div>
  );
};
